import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import axios from '../../../services/axios';
import Modal from '../../../components/Modal/Modal';
import EmployeeNameSearch from '../../../components/EmployeeNameSearch/';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field } from '../../../styles/forms/Field';

// =============================================================================
// Constantes
// =============================================================================

// Status de parcela disponíveis para seleção no modal.
// PENDENTE → demonstrativo do que será descontado no mês.
// PAGO     → comprovante do que já foi efetivamente descontado.
const INSTALLMENT_STATUS_OPTIONS = [
   { value: 'PENDENTE', label: '📋 Pendente — o que será descontado' },
   { value: 'PAGO',     label: '✅ Pago — o que já foi descontado'   },
];

// =============================================================================
// Componente
// =============================================================================

/**
 * Modal para gerar o PDF de descontos mensais de um funcionário.
 *
 * O usuário seleciona:
 *   - Funcionário via autocomplete
 *   - Mês de referência (input type="month")
 *   - Status das parcelas: PENDENTE ou PAGO
 *
 * O PDF é gerado no backend (Puppeteer) e aberto em nova aba.
 *
 * Props:
 *   isOpen  {boolean} — controla visibilidade
 *   onClose {fn}      — chamado ao fechar
 */
export default function MonthlyDeductionsModal({ isOpen, onClose }) {
   const [selectedEmployee, setSelectedEmployee] = useState(null);
   const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
   const [referenceMonth, setReferenceMonth] = useState('');
   const [installmentStatusFilter, setInstallmentStatusFilter] = useState('PENDENTE');
   const [isGenerating, setIsGenerating] = useState(false);

   // ─── Fechar e limpar estado ────────────────────────────────────
   const handleClose = useCallback(() => {
      setSelectedEmployee(null);
      setEmployeeSearchQuery('');
      setReferenceMonth('');
      setInstallmentStatusFilter('PENDENTE');
      onClose();
   }, [onClose]);

   // ─── Gerar PDF ────────────────────────────────────────────────
   const handleGeneratePdf = async () => {
      if (!selectedEmployee) {
         toast.error('Selecione um funcionário.');
         return;
      }
      if (!referenceMonth) {
         toast.error('Selecione o mês de referência.');
         return;
      }

      setIsGenerating(true);
      try {
         const response = await axios.get(
            `/hr/employees/${selectedEmployee.id}/monthly-deductions-pdf`,
            {
               params: {
                  reference_month: referenceMonth,
                  installment_status: installmentStatusFilter,
               },
               responseType: 'arraybuffer',
            },
         );

         const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
         const pdfUrl = URL.createObjectURL(pdfBlob);
         window.open(pdfUrl, '_blank');
      } catch (error) {
         const errorMessage =
            error?.response?.data?.errors?.[0] ??
            error?.response?.data?.message ??
            error?.message ??
            'Erro ao gerar PDF.';
         toast.error(errorMessage);
      } finally {
         setIsGenerating(false);
      }
   };

   return (
      <Modal
         isOpen={isOpen}
         title="Descontos mensais do funcionário"
         onClose={handleClose}
         maxWidth="480px"
      >
         <FormGrid $cols={12} $colsMobile={12}>

            {/* ─── Funcionário ──────────────────────────────────── */}
            <Field $col={12} $colMobile={12}>
               <label>Funcionário *</label>
               <EmployeeNameSearch
                  value={employeeSearchQuery}
                  onChange={setEmployeeSearchQuery}
                  onSelect={(employee) => {
                     setSelectedEmployee(employee);
                     setEmployeeSearchQuery(employee?.full_name ?? '');
                  }}
                  required
                  placeholder="Digite o nome do funcionário..."
                  showHint
               />
            </Field>

            {/* ─── Mês de referência ────────────────────────────── */}
            <Field $col={6} $colMobile={12}>
               <label htmlFor="monthly-pdf-reference-month">
                  Mês de referência *
               </label>
               <input
                  id="monthly-pdf-reference-month"
                  type="month"
                  value={referenceMonth}
                  onChange={(e) => setReferenceMonth(e.target.value)}
               />
            </Field>

            {/* ─── Status das parcelas ──────────────────────────── */}
            <Field $col={6} $colMobile={12}>
               <label htmlFor="monthly-pdf-installment-status">
                  Exibir parcelas *
               </label>
               <select
                  id="monthly-pdf-installment-status"
                  value={installmentStatusFilter}
                  onChange={(e) => setInstallmentStatusFilter(e.target.value)}
               >
                  {INSTALLMENT_STATUS_OPTIONS.map((option) => (
                     <option key={option.value} value={option.value}>
                        {option.label}
                     </option>
                  ))}
               </select>
            </Field>

            {/* ─── Ações ───────────────────────────────────────── */}
            <Field $col={12} $colMobile={12}>
               <div
                  style={{
                     display: 'flex',
                     justifyContent: 'flex-end',
                     gap: '10px',
                     paddingTop: '4px',
                  }}
               >
                  <button
                     type="button"
                     onClick={handleClose}
                     disabled={isGenerating}
                  >
                     Cancelar
                  </button>
                  <button
                     type="button"
                     onClick={handleGeneratePdf}
                     disabled={isGenerating}
                  >
                     {isGenerating ? 'Gerando PDF...' : '🖨️ Gerar PDF'}
                  </button>
               </div>
            </Field>

         </FormGrid>
      </Modal>
   );
}

MonthlyDeductionsModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
};
